import { useState } from 'react';
import { api, ErreurApi } from '../lib/api';
import { ouvrirLien, type ContexteFront } from '../lib/front';
import { date as formateDate } from '../lib/format';

/**
 * La boîte d'actions, identique dans tout le plugin.
 *
 * Six gestes, toujours les mêmes, toujours au même endroit : le commercial ne
 * doit pas réapprendre l'interface en changeant d'onglet. Deux d'entre eux sont
 * de simples liens — ouvrir la conversation, ouvrir le projet — et
 * n'enregistrent rien ; les quatre autres écrivent une décision que le pipeline
 * relira.
 *
 * Rien ne part chez un client : « Confier à Claude » dépose un brouillon dans
 * Front, l'envoi reste un geste humain.
 */

/** Ce que la boîte sait viser. Au moins un champ doit être renseigné. */
export interface CibleAction {
  conv_id?: string | null;
  /** Conversations du dossier, quand la cible elle-même n'en porte pas. */
  conversations?: Array<{
    id: string;
    sujet?: string | null;
    /** Dernier échange. Un fil de 2019 ne s'ouvre pas comme un fil d'hier. */
    date?: string | null;
    lien_front: string;
  }>;
  tiers_id?: number | null;
  projet_ref?: string | null;
  /** Autres projets Dolibarr du dossier. */
  projets?: Array<{ ref: string; lien_dolibarr: string }>;
  domaine?: string | null;
  client?: string | null;
  /**
   * Ce qui a déjà été expliqué sur cette cible. Servi avec l'élément, en une
   * requête pour tout l'écran : la boîte est rendue des dizaines de fois par
   * page, une requête par ligne saturerait l'API pour rien.
   */
  explications?: Array<{ texte: string; auteur: string | null; cree_le: string | null }>;
  /** Dernier échange sur la conversation visée, quand l'écran le connaît. */
  date?: string | null;
  lien_front?: string | null;
  lien_dolibarr?: string | null;
}

type Ouvert = null | 'affectation' | 'explication' | 'abandon' | 'relance';

export function BoiteActions({
  cible,
  contexteFront,
  auteur,
  onFait,
  compact = false,
}: {
  cible: CibleAction;
  contexteFront: ContexteFront | undefined;
  auteur?: string;
  /** Rappelé après toute écriture, pour recharger la vue. */
  onFait?: () => void;
  compact?: boolean;
}) {
  const [ouvert, setOuvert] = useState<Ouvert>(null);
  const [texte, setTexte] = useState('');
  const [qui, setQui] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [fait, setFait] = useState<string | null>(null);
  const [commerciaux, setCommerciaux] = useState<string[]>([]);
  // Repliees par defaut : la boite doit rester lisible d'un coup d'oeil, et un
  // dossier a parfois dix fils. On les deplie quand on les cherche.
  const [filsOuverts, setFilsOuverts] = useState(false);
  const [projetsOuverts, setProjetsOuverts] = useState(false);
  /**
   * Ce qu'on vient d'écrire, affiché sans attendre le rechargement.
   *
   * Une explication saisie ici disparaissait de l'écran : le pipeline la
   * relisait bien, mais celui qui l'avait écrite n'en voyait plus la trace et
   * la réécrivait au passage suivant. Elle revient ensuite avec l'élément, à
   * la prochaine lecture.
   */
  const [ajoutees, setAjoutees] = useState<
    Array<{ texte: string; auteur: string | null; cree_le: string | null }>
  >([]);

  /**
   * La liste des collaborateurs est chargée à l'ouverture du panneau, pas au
   * montage : la boîte est rendue des dizaines de fois par écran, et personne
   * n'ouvre l'affectation sur toutes les lignes. `gens` est mémorisé par le
   * cache de `appel`, donc une seule requête pour toute la session.
   */
  const chargerCommerciaux = async () => {
    if (commerciaux.length) return;
    try {
      const r = await api.collaborateurs();
      setCommerciaux(r.elements.map((x) => x.nom).filter(Boolean));
    } catch {
      // Sans la liste, le champ reste vide et l'affectation est impossible :
      // c'est préférable à une liste inventée.
    }
  };

  const fils = [
    ...(cible.lien_front && cible.conv_id
      ? [{
          id: cible.conv_id,
          sujet: null as string | null,
          date: cible.date ?? null,
          lien_front: cible.lien_front,
        }]
      : []),
    ...(cible.conversations ?? []),
  ].filter((f, i, tout) => f.id && tout.findIndex((x) => x.id === f.id) === i);
  const projets = [
    ...(cible.lien_dolibarr && cible.projet_ref
      ? [{ ref: cible.projet_ref, lien_dolibarr: cible.lien_dolibarr }]
      : []),
    ...(cible.projets ?? []),
  ];

  const base = {
    conv_id: cible.conv_id ?? undefined,
    tiers_id: cible.tiers_id ?? undefined,
    projet_ref: cible.projet_ref ?? undefined,
    domaine: cible.domaine ?? undefined,
    client: cible.client ?? undefined,
    auteur,
  };

  const agir = async (
    action: () => Promise<{ message?: string }>,
    resume: string,
  ) => {
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await action();
      setFait(r.message || resume);
      setOuvert(null);
      setTexte('');
      onFait?.();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Action impossible.');
    } finally {
      setEnvoi(false);
    }
  };

  const dites = [...(cible.explications ?? []), ...ajoutees];
  const rappel = dites.length > 0 && (
    <ul className="actions-dites">
      {dites.map((d, i) => (
        <li key={`${d.cree_le ?? ''}-${i}`} title={d.texte}>
          <span className="actions-dites-marque">Expliqué</span>
          {d.texte}
          {d.auteur && <span className="actions-dites-qui"> — {d.auteur}</span>}
        </li>
      ))}
    </ul>
  );

  if (fait) {
    return (
      <div className={`actions${compact ? ' actions--compact' : ''}`}>
        {rappel}
        <p className="actions-fait">{fait}</p>
        <button type="button" className="lien" onClick={() => setFait(null)}>
          Autre action
        </button>
      </div>
    );
  }

  // Deux colonnes, et la separation n'est pas decorative : a gauche ce qui
  // emmene ailleurs sans rien changer, a droite ce qui engage une decision.
  // Un clic malheureux a gauche fait perdre une seconde, a droite il ecrit.
  const aDesLiens = fils.length > 0 || projets.length > 0;

  // Une relance a besoin d'un fil auquel repondre. Une tache issue de Dolibarr
  // — devis muet, lead non repris, client dormant — n'en porte pas elle-meme,
  // mais le dossier en porte souvent : on prend le premier. Et quand il n'y en
  // a vraiment aucun, le bouton reste, grise et explique. Il disparaissait
  // jusqu'ici, ce qui donnait a croire que l'ecran l'avait oublie.
  const convRelance = cible.conv_id ?? fils[0]?.id ?? null;

  return (
    <div className={`actions${compact ? ' actions--compact' : ''}`}>
      {rappel}
      <div className={`actions-grille${aDesLiens ? '' : ' actions-grille--seule'}`}>
        {aDesLiens && (
          <div className="actions-col">
            {/*
              Un seul fil : un bouton qui l'ouvre. Plusieurs : un en-tete qui
              deplie la liste complete. Il servait jusqu'ici de lien deguise
              vers le premier fil, et les autres s'affichaient dessous — on
              annoncait trois conversations, on n'en montrait que deux, et la
              troisieme se cachait derriere le compte.
            */}
            {fils.length === 1 && (
              <button
                type="button"
                className="action action--conversation"
                onClick={() => ouvrirLien(contexteFront, fils[0]!.lien_front)}
                title={fils[0]!.sujet || fils[0]!.id}
              >
                Conversation
                {fils[0]!.date && (
                  <span className="action-date">{formateDate(fils[0]!.date)}</span>
                )}
              </button>
            )}

            {fils.length > 1 && (
              <>
                <button
                  type="button"
                  className="action action--conversation action--pliable"
                  onClick={() => setFilsOuverts(!filsOuverts)}
                  aria-expanded={filsOuverts}
                >
                  <span className="action-chevron">{filsOuverts ? '▾' : '▸'}</span>
                  Conversations ({fils.length})
                </button>
                {filsOuverts &&
                  fils.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className="action action--conversation action--secondaire"
                      onClick={() => ouvrirLien(contexteFront, f.lien_front)}
                      title={f.sujet || f.id}
                    >
                      {(f.sujet || f.id).slice(0, 24)}
                      {f.date && <span className="action-date">{formateDate(f.date)}</span>}
                    </button>
                  ))}
              </>
            )}

            {projets.length === 1 && (
              <button
                type="button"
                className="action action--dolibarr"
                onClick={() => ouvrirLien(contexteFront, projets[0]!.lien_dolibarr)}
                title={`Ouvrir ${projets[0]!.ref} dans Dolibarr`}
              >
                Dolibarr
                <span className="action-date">{projets[0]!.ref}</span>
              </button>
            )}

            {projets.length > 1 && (
              <>
                <button
                  type="button"
                  className="action action--dolibarr action--pliable"
                  onClick={() => setProjetsOuverts(!projetsOuverts)}
                  aria-expanded={projetsOuverts}
                >
                  <span className="action-chevron">{projetsOuverts ? '▾' : '▸'}</span>
                  Dolibarr ({projets.length})
                </button>
                {projetsOuverts &&
                  projets.map((pr) => (
                    <button
                      key={pr.ref}
                      type="button"
                      className="action action--dolibarr action--secondaire"
                      onClick={() => ouvrirLien(contexteFront, pr.lien_dolibarr)}
                      title={`Ouvrir ${pr.ref} dans Dolibarr`}
                    >
                      {pr.ref}
                    </button>
                  ))}
              </>
            )}
          </div>
        )}

        <div className="actions-col">
          <button
            type="button"
            className="action action--affectation"
            onClick={() => {
              void chargerCommerciaux();
              setOuvert(ouvert === 'affectation' ? null : 'affectation');
            }}
            aria-expanded={ouvert === 'affectation'}
          >
            Chef de projet
          </button>

          <button
            type="button"
            className="action action--accent"
            disabled={!convRelance}
            title={
              convRelance
                ? 'Claude rédige la relance et la dépose en brouillon.'
                : 'Aucune conversation rattachée : il n’y a pas de fil auquel répondre.'
            }
            onClick={() => setOuvert(ouvert === 'relance' ? null : 'relance')}
            aria-expanded={ouvert === 'relance'}
          >
            Confier à Claude
          </button>

          <button
            type="button"
            className="action action--explication"
            onClick={() => setOuvert(ouvert === 'explication' ? null : 'explication')}
            aria-expanded={ouvert === 'explication'}
          >
            Expliquer
          </button>

          <button
            type="button"
            className="action action--abandon"
            onClick={() => setOuvert(ouvert === 'abandon' ? null : 'abandon')}
            aria-expanded={ouvert === 'abandon'}
          >
            Abandonner
          </button>
        </div>
      </div>

      {ouvert === 'affectation' && (
        <div className="action-panneau">
          <label className="aide" htmlFor={`aff-${cible.conv_id ?? cible.tiers_id}`}>
            Qui reprend ce dossier ?
          </label>
          <select
            id={`aff-${cible.conv_id ?? cible.tiers_id}`}
            className="champ"
            value={qui}
            onChange={(e) => setQui(e.target.value)}
            disabled={envoi}
          >
            <option value="">choisir…</option>
            {(commerciaux ?? []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="bouton"
            disabled={envoi || !qui}
            onClick={() =>
              agir(
                () => api.agir({ ...base, type: 'affectation', au_nom_de: qui }),
                `${qui} reprend le sujet.`,
              )
            }
          >
            {envoi ? 'Enregistrement…' : 'Affecter'}
          </button>
        </div>
      )}

      {ouvert === 'relance' && convRelance && (
        <div className="action-panneau">
          <label className="aide" htmlFor={`rel-${convRelance}`}>
            Ce que vous voulez dire. Facultatif.
          </label>
          <textarea
            id={`rel-${convRelance}`}
            className="champ"
            rows={2}
            maxLength={2000}
            value={texte}
            placeholder="Par exemple : proposer un point téléphonique."
            onChange={(e) => setTexte(e.target.value)}
            disabled={envoi}
          />
          <div className="action-panneau-boutons">
            <button
              type="button"
              className="bouton"
              disabled={envoi}
              onClick={() =>
                agir(
                  () =>
                    api.demander({
                      type: 'relance',
                      conv_id: convRelance,
                      client: cible.client ?? undefined,
                      instruction: texte.trim() || undefined,
                      au_nom_de: auteur,
                      auteur,
                    }),
                  'Relance confiée.',
                )
              }
            >
              Relance
            </button>
            <button
              type="button"
              className="bouton bouton--discret"
              disabled={envoi}
              onClick={() =>
                agir(
                  () =>
                    api.demander({
                      type: 'reprise_contact',
                      conv_id: convRelance,
                      client: cible.client ?? undefined,
                      instruction: texte.trim() || undefined,
                      au_nom_de: auteur,
                      auteur,
                    }),
                  'Reprise de contact confiée.',
                )
              }
            >
              Reprise de contact
            </button>
          </div>
          <p className="aide">Un brouillon sera déposé dans Front. Rien ne part sans vous.</p>
        </div>
      )}

      {ouvert === 'explication' && (
        <div className="action-panneau">
          <label className="aide" htmlFor={`exp-${cible.conv_id ?? cible.tiers_id}`}>
            Dites-le comme vous le diriez. Le pipeline s’y rangera.
          </label>
          <textarea
            id={`exp-${cible.conv_id ?? cible.tiers_id}`}
            className="champ"
            rows={3}
            maxLength={2000}
            value={texte}
            placeholder="Par exemple : dans Dolibarr elle s’appelle X. Ou : dossier clos, le client a acheté ailleurs."
            onChange={(e) => setTexte(e.target.value)}
            disabled={envoi}
          />
          <button
            type="button"
            className="bouton"
            disabled={envoi || !texte.trim()}
            onClick={() => {
              const dit = texte.trim();
              void agir(async () => {
                const r = await api.agir({ ...base, type: 'explication', texte: dit });
                setAjoutees((avant) => [
                  ...avant,
                  { texte: dit, auteur: auteur ?? null, cree_le: null },
                ]);
                return r;
              }, 'Explication enregistrée.');
            }}
          >
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      )}

      {ouvert === 'abandon' && (
        <div className="action-panneau">
          <p className="aide">
            Le sujet sortira des listes et n’y reviendra pas. Vous pouvez dire
            pourquoi, ce n’est pas obligatoire.
          </p>
          <textarea
            className="champ"
            rows={2}
            maxLength={2000}
            value={texte}
            placeholder="Par exemple : ce n’est pas un dossier commercial."
            onChange={(e) => setTexte(e.target.value)}
            disabled={envoi}
          />
          <button
            type="button"
            className="bouton bouton--discret"
            disabled={envoi}
            onClick={() =>
              agir(
                () =>
                  api.agir({
                    ...base,
                    type: 'abandon',
                    texte: texte.trim() || 'dossier non commercial',
                  }),
                'Sujet abandonné.',
              )
            }
          >
            {envoi ? 'Retrait…' : 'Abandonner ce sujet'}
          </button>
        </div>
      )}

      {erreur && <p className="erreur-texte">{erreur}</p>}
    </div>
  );
}
